// C10: the screen locks after 15 minutes without use, and a sign-in ends after 12 hours (doctors, reception) or 8 (clinic admins,
// super admin) or 12 hours (reception). Both give a 1-minute warning. Locking never closes the page, so anything
// typed and not yet saved stays on screen: the person re-enters their password and carries on.
// The time limit is also enforced by the database rules and the server (functions/src/session.ts).
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EmailAuthProvider, reauthenticateWithCredential, type MultiFactorResolver } from 'firebase/auth';
import { resolveWithCode, resolverFor } from '../../services/twoStepService';
import { startSecondStep } from '../../services/secondStepService';
import { Loader2, Lock } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useT } from '../../i18n';
import { auth } from '../../lib/firebase';
import { IDLE_LOCK_MINUTES, WARN_SECONDS, maxSessionMs } from '../../config/session';

type Lock = null | 'idle' | 'expired';

export function SessionGuard() {
  const { role, logout } = useApp();
  const t = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const lastActive = useRef(Date.now());
  const [authStart, setAuthStart] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [locked, setLocked] = useState<Lock>(null);
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // 2-step login on: after the password, the code from the authenticator app.
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const [code, setCode] = useState('');

  // When did this sign-in start? (from the login token; demo has none, so only the idle lock applies there)
  const readAuthTime = useCallback(async () => {
    const u = auth?.currentUser;
    if (!u) return setAuthStart(null);
    const r = await u.getIdTokenResult();
    setAuthStart(Date.parse(r.authTime));
  }, []);
  useEffect(() => { void readAuthTime(); }, [readAuthTime]);

  // Any touch, click, key or scroll counts as use (only while unlocked).
  useEffect(() => {
    const mark = () => { if (!locked) lastActive.current = Date.now(); };
    const evs = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'];
    evs.forEach((e) => window.addEventListener(e, mark, { passive: true, capture: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, mark, { capture: true }));
  }, [locked]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const idleLeft = IDLE_LOCK_MINUTES * 60_000 - (now - lastActive.current);
  const maxLeft = authStart ? authStart + maxSessionMs(role) - now : Infinity;
  useEffect(() => {
    if (locked) return;
    if (maxLeft <= 0) setLocked('expired');
    else if (idleLeft <= 0) setLocked('idle');
  }, [idleLeft, maxLeft, locked]);

  // While locked, nothing behind the lock can be reached by keyboard or screen reader.
  useEffect(() => {
    const app = document.getElementById('sc-app');
    if (app) app.inert = !!locked;
  }, [locked]);

  const unlock = async (e: FormEvent) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const u = auth?.currentUser;
      if (resolver) {
        await resolveWithCode(resolver, code);
        setResolver(null);
        setCode('');
        await u?.getIdToken(true);
        await readAuthTime();
      } else if (u?.email) {
        const before = authStart;
        try {
          await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, pw));
        } catch (x) {
          const r = resolverFor(x);
          if (!r) throw x;
          setResolver(r);
          setPw('');
          return;
        }
        await u.getIdToken(true);
        await readAuthTime();
        // A password unlock is a new sign-in to the login service: carry this device's email-code check over.
        if (before) {
          const r = await startSecondStep(u.uid, Math.floor(before / 1000)).catch(() => ({ done: false }));
          if (!r.done) { window.location.reload(); return; }
        }
      } else if (!pw) {
        throw new Error('empty');
      }
      lastActive.current = Date.now();
      setPw('');
      setLocked(null);
      await qc.invalidateQueries();
    } catch {
      setErr(t('session.wrongPassword'));
    } finally {
      setBusy(false);
    }
  };
  const signOutNow = () => { setLocked(null); logout(); navigate('/login'); };

  const warnIdle = !locked && idleLeft > 0 && idleLeft <= WARN_SECONDS * 1000;
  const warnMax = !locked && maxLeft > 0 && maxLeft <= WARN_SECONDS * 1000;
  const email = auth?.currentUser?.email ?? '';

  return (
    <>
      {(warnIdle || warnMax) && (
        <div role="alertdialog" aria-live="assertive" data-testid="session-warning" className="fixed inset-x-3 bottom-24 z-50 mx-auto max-w-md rounded-2xl border border-status-warn-line bg-status-warn-soft p-4 shadow-lg lg:bottom-6">
          <p className="font-semibold text-status-warn">
            {warnMax ? t('session.warnMax', { s: Math.ceil(maxLeft / 1000) }) : t('session.warnIdle', { s: Math.ceil(idleLeft / 1000) })}
          </p>
          <p className="mt-1 text-sm text-slate-700">{t('session.draftsKept')}</p>
          {warnIdle && !warnMax && (
            <button type="button" onClick={() => { lastActive.current = Date.now(); setNow(Date.now()); }} data-testid="session-stay" className="mt-3 h-12 w-full rounded-xl bg-sc-teal text-sm font-bold text-white">{t('session.stay')}</button>
          )}
        </div>
      )}
      {locked && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-sc-bg px-5" data-testid="session-lock">
          <form onSubmit={unlock} className="w-full max-w-sm rounded-3xl border border-sc-line bg-white p-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sc-soft text-sc-teal"><Lock className="h-6 w-6" /></span>
            <h1 className="mt-4 text-xl font-bold text-slate-900">{locked === 'expired' ? t('session.expiredTitle') : t('session.lockedTitle')}</h1>
            <p className="mt-1 text-sm text-slate-600">{locked === 'expired' ? t('session.expiredBody') : t('session.lockedBody')}</p>
            {email && <p className="mt-3 break-all text-sm font-semibold text-slate-800">{email}</p>}
            {resolver ? (
              <label className="mt-3 block">
                <span className="mb-1 block text-sm font-semibold text-slate-700">{t('twostep.code')}</span>
                <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus data-testid="session-code" className="h-12 w-full rounded-xl border border-sc-line px-4 text-center text-2xl tracking-[0.4em] focus:border-sc-teal focus:outline-none focus:ring-2 focus:ring-sc-teal/30" />
              </label>
            ) : (
              <label className="mt-3 block">
                <span className="mb-1 block text-sm font-semibold text-slate-700">{t('login.password')}</span>
                <input type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus data-testid="session-password" className="h-12 w-full rounded-xl border border-sc-line px-4 text-base focus:border-sc-teal focus:outline-none focus:ring-2 focus:ring-sc-teal/30" />
              </label>
            )}
            {err && <p className="mt-2 text-sm font-medium text-status-urgent" role="alert">{err}</p>}
            <button type="submit" disabled={busy} data-testid="session-unlock" className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-sc-teal font-bold text-white disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t('session.unlock')}
            </button>
            <button type="button" onClick={signOutNow} data-testid="session-signout" className="mt-2 h-11 w-full text-sm font-semibold text-slate-600">{t('session.signOut')}</button>
          </form>
        </div>
      )}
    </>
  );
}
